import { FormEvent, useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel } from "../components/Panel";
import {
  createRuntimeDepartment,
  fetchRuntimeWorkspace,
  type RuntimeWorkspaceOverview
} from "../lib/api/runtimeWorkspaceClient";
import { useWorkspaceLanguage } from "../lib/i18n";
import { hasExecutivePerspective, isBillablePerspective } from "../lib/valueWorkflow";
import type { PerspectiveType } from "../types";

export function DepartmentNew() {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "Invite department",
    title: "Create department",
    description: "Each department receives its own access. Departments do not see other department perspectives.",
    loadError: "Workspace data could not be loaded.",
    noWorkspace: "No workspace available yet.",
    limitReached: "The department limit for this workspace has been reached.",
    executiveExists: "Executive Management perspective already exists for this workspace.",
    missingName: "Please enter the department name.",
    created: "Perspective was created.",
    createError: "Department could not be created.",
    loadingTitle: "Workspace is loading",
    loadingBody: "Workspace data is loading...",
    noWorkspaceBody: "There is currently no workspace stored for your access.",
    panelTitle: "New department",
    name: "Name",
    placeholder: "e.g. Sales, Marketing, Delivery, Finance",
    perspectiveType: "Perspective type",
    contributorEmail: "Contributor email",
    contributorEmailPlaceholder: "name@company.com",
    votingPerspective: "Voting perspective",
    votingHelp: "Disable this only for project coordination without functional input.",
    note: "Invited people answer the same value questions as other perspectives. The existing workspace remains unchanged.",
    submit: "Create perspective",
    setupSent: "Setup email was sent.",
    setupCreated: "Setup link was created.",
    setupValidUntil: "Setup link valid until",
    copySetupLink: "Copy setup link",
    perspectiveLabels: {
      department: "Department",
      executive: "Executive Management",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Additional Perspective"
    } satisfies Record<PerspectiveType, string>,
    defaults: {
      department: "Department",
      executive: "Executive Management",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Additional Perspective"
    } satisfies Record<PerspectiveType, string>
  } : {
    eyebrow: "Abteilung einladen",
    title: "Abteilung anlegen",
    description: "Jede Abteilung erhält einen eigenen Zugang. Abteilungen sehen keine anderen Abteilungsperspektiven.",
    loadError: "Workspace-Daten konnten nicht geladen werden.",
    noWorkspace: "Noch kein Workspace vorhanden.",
    limitReached: "Das Abteilungslimit dieses Workspace ist erreicht.",
    executiveExists: "Geschäftsleitungsperspektive ist für diesen Workspace bereits vorhanden.",
    missingName: "Bitte den Namen der Abteilung ausfüllen.",
    created: "Perspektive wurde angelegt.",
    createError: "Abteilung konnte nicht angelegt werden.",
    loadingTitle: "Workspace wird geladen",
    loadingBody: "D1-Daten werden geladen...",
    noWorkspaceBody: "Für Ihren Zugang ist aktuell noch kein Workspace in D1 hinterlegt.",
    panelTitle: "Neue Abteilung",
    name: "Name",
    placeholder: "z. B. Sales, Marketing, Delivery, Finance",
    perspectiveType: "Perspektiventyp",
    contributorEmail: "Contributor E-Mail",
    contributorEmailPlaceholder: "name@unternehmen.de",
    votingPerspective: "Stimmberechtigte Perspektive",
    votingHelp: "Nur bei Projektleitung ohne fachliche Eingabe deaktivieren.",
    note: "Eingeladene Personen beantworten dieselben Value-Fragen wie andere Perspektiven. Der bestehende Workspace bleibt unverändert.",
    submit: "Perspektive anlegen",
    setupSent: "Setup-Mail wurde gesendet.",
    setupCreated: "Setup-Link wurde erzeugt.",
    setupValidUntil: "Setup-Link gültig bis",
    copySetupLink: "Setup-Link kopieren",
    perspectiveLabels: {
      department: "Abteilung",
      executive: "Geschäftsleitung",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Weitere Perspektive"
    } satisfies Record<PerspectiveType, string>,
    defaults: {
      department: "Abteilung",
      executive: "Geschäftsleitung",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Weitere Perspektive"
    } satisfies Record<PerspectiveType, string>
  };
  const [initialPerspective] = useState<PerspectiveType>(() => {
    const requested = new URLSearchParams(window.location.search).get("perspective");
    return isPerspectiveType(requested) ? requested : "department";
  });
  const [overview, setOverview] = useState<RuntimeWorkspaceOverview>({ departments: [] });
  const [perspectiveType, setPerspectiveType] = useState<PerspectiveType>(initialPerspective);
  const [name, setName] = useState(copy.defaults[initialPerspective]);
  const [isVotingPerspective, setIsVotingPerspective] = useState(true);
  const [createdSetupLink, setCreatedSetupLink] = useState<string | undefined>();
  const [createdSetupExpiry, setCreatedSetupExpiry] = useState<string | undefined>();
  const [createdEmailSent, setCreatedEmailSent] = useState<boolean | undefined>();
  const [createdEmailError, setCreatedEmailError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;

    async function loadWorkspace() {
      setIsLoading(true);
      setError(undefined);
      try {
        const nextOverview = await fetchRuntimeWorkspace();
        if (isMounted) setOverview(nextOverview);
      } catch (caught) {
        if (isMounted) setError(caught instanceof Error ? caught.message : copy.loadError);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadWorkspace();

    return () => {
      isMounted = false;
    };
  }, [copy.loadError]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(undefined);
    setError(undefined);
    const workspace = overview.workspace;
    if (!workspace) {
      setError(copy.noWorkspace);
      return;
    }
    const billableCount = overview.departments.filter(isBillablePerspective).length;
    if (isBillablePerspective({ perspectiveType, isVotingPerspective }) && billableCount >= workspace.maxDepartments) {
      setError(copy.limitReached);
      return;
    }
    if (perspectiveType === "executive" && hasExecutivePerspective(overview.departments)) {
      setError(copy.executiveExists);
      return;
    }

    const formData = new FormData(event.currentTarget);
    const submittedName = String(formData.get("name") ?? "").trim();
    const contributorEmail = String(formData.get("contributorEmail") ?? "").trim();
    if (!submittedName) {
      setError(copy.missingName);
      return;
    }

    setIsSaving(true);
    try {
      const result = await createRuntimeDepartment({
        workspaceId: workspace.id,
        name: submittedName,
        perspectiveType,
        contributorEmail: contributorEmail || undefined,
        isVotingPerspective,
        language
      });
      setOverview((current) => ({ ...current, departments: [...current.departments, result.department] }));
      setCreatedSetupLink(result.setupLink);
      setCreatedSetupExpiry(result.expiresAt);
      setCreatedEmailSent(result.emailSent);
      setCreatedEmailError(result.emailError);
      setNotice(copy.created);
      event.currentTarget.reset();
      setPerspectiveType(initialPerspective);
      setName(copy.defaults[initialPerspective]);
      setIsVotingPerspective(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.createError);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Layout
      eyebrow={copy.eyebrow}
      title={copy.title}
      description={copy.description}
    >
      {isLoading ? <Panel title={copy.loadingTitle}><p>{copy.loadingBody}</p></Panel> : null}
      {!isLoading && !overview.workspace ? (
        <Panel title={copy.noWorkspace} variant="accent">
          <p>{copy.noWorkspaceBody}</p>
        </Panel>
      ) : null}
      {overview.workspace ? (
        <Panel title={copy.panelTitle} variant="accent">
          {perspectiveType === "executive" && hasExecutivePerspective(overview.departments) ? (
            <p className="form-error" role="alert">{copy.executiveExists}</p>
          ) : null}
          <form className="form form-grid" onSubmit={handleSubmit}>
            <label>
              {copy.name}
              <input
                name="name"
                placeholder={copy.placeholder}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label>
              {copy.perspectiveType}
              <select
                value={perspectiveType}
                onChange={(event) => {
                  const nextType = event.target.value as PerspectiveType;
                  setPerspectiveType(nextType);
                  setName((current) => current.trim() && current !== copy.defaults[perspectiveType] ? current : copy.defaults[nextType]);
                }}
              >
                {(Object.keys(copy.perspectiveLabels) as PerspectiveType[]).map((type) => (
                  <option key={type} value={type}>{copy.perspectiveLabels[type]}</option>
                ))}
              </select>
            </label>
            <label>
              {copy.contributorEmail}
              <input name="contributorEmail" type="email" placeholder={copy.contributorEmailPlaceholder} />
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={isVotingPerspective}
                onChange={(event) => setIsVotingPerspective(event.target.checked)}
              />
              <span>{copy.votingPerspective}</span>
            </label>
            <p className="form-note">
              {copy.votingHelp} {copy.note}
            </p>
            {notice ? <p className="form-note">{notice}</p> : null}
            {createdSetupLink ? (
              <div className="form-wide">
                <p className="form-note">
                  {createdEmailSent ? copy.setupSent : (createdEmailError ?? copy.setupCreated)}
                </p>
                {createdSetupExpiry ? (
                  <p className="form-note">
                    {copy.setupValidUntil} {new Date(createdSetupExpiry).toLocaleString(language === "en" ? "en-US" : "de-DE")}
                  </p>
                ) : null}
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={() => void navigator.clipboard?.writeText(createdSetupLink)}
                >
                  {copy.copySetupLink}
                </button>
              </div>
            ) : null}
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            <button className="button button-primary" type="submit" disabled={isSaving || (perspectiveType === "executive" && hasExecutivePerspective(overview.departments))}>
              {copy.submit}
            </button>
          </form>
        </Panel>
      ) : null}
    </Layout>
  );
}

function isPerspectiveType(value: string | null): value is PerspectiveType {
  return value === "department" || value === "executive" || value === "finance" || value === "commercial" || value === "operations" || value === "other";
}
